import { useState, useMemo, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import FilteredPostsDisplay from '../FilteredPostsDisplay';
import { apiPost } from '../../services/apiClient';

function deltaBadgeClass(delta) {
    if (delta == null) return 'new';
    if (delta > 0) return 'positive';
    if (delta < 0) return 'negative';
    return 'new';
}

function npsBadgeClass(nps) {
    if (nps >= 50) return 'positive';
    if (nps <= -10) return 'negative';
    return 'neutral';
}

/**
 * Summary card: quarter comparison with stacked gains/drops
 */
function BestWorstQuarterSummary({ gains, drops, quarter, prevQuarter, onClick, error }) {
    if (error) {
        return (
            <div className="dim-widget-error">
                <div>Unable to load quarterly data.</div>
            </div>
        );
    }

    const hasData = gains.length > 0 || drops.length > 0;
    const comparisonLabel = quarter && prevQuarter ? `${quarter} vs ${prevQuarter}` : quarter || '';

    return (
        <div
            className="dim-widget-card quarter"
            onClick={onClick}
            role="region"
            aria-label="Quarter over Quarter NPS"
        >
            <div className="dim-widget-header">
                <span className="widget-icon">📈</span>
                Quarter over Quarter
            </div>
            <div className="dim-widget-headline">
                {comparisonLabel || 'Quarterly NPS Trends'}
            </div>
            <div className="dim-widget-subtitle">
                {quarter ? `Latest quarter: ${quarter}` : 'Awaiting quarterly data'}
            </div>
            <div className="dim-widget-body">
                {hasData ? (<>
                    {gains.length > 0 && (<>
                        <div className="dim-section-header gains">Biggest Gains</div>
                        <ul className="dim-widget-list">
                            {gains.map(row => (
                                <li key={row.dimension_value}>
                                    <span className="item-name">{row.dimension_value}</span>
                                    <span className={`delta-badge ${deltaBadgeClass(row.delta)}`}
                                          aria-label={`NPS improved by ${row.delta} points`}>
                                        ↑ +{row.delta}
                                    </span>
                                    <span className={`nps-badge ${npsBadgeClass(row.nps)}`} style={{ marginLeft: 6 }}>
                                        {row.nps}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </>)}
                    {drops.length > 0 && (<>
                        <div className="dim-section-header drops">Biggest Drops</div>
                        <ul className="dim-widget-list">
                            {drops.map(row => (
                                <li key={row.dimension_value}>
                                    <span className="item-name">{row.dimension_value}</span>
                                    <span className={`delta-badge ${deltaBadgeClass(row.delta)}`}
                                          aria-label={`NPS decreased by ${Math.abs(row.delta)} points`}>
                                        ↓ {row.delta}
                                    </span>
                                    <span className={`nps-badge ${npsBadgeClass(row.nps)}`} style={{ marginLeft: 6 }}>
                                        {row.nps}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </>)}
                </>) : (
                    <div className="dim-widget-empty">
                        <div className="empty-icon">📅</div>
                        Not enough quarterly data for comparison. At least two quarters needed.
                    </div>
                )}
            </div>
            {hasData && (
                <div className="dim-widget-footer">
                    <button
                        aria-expanded={false}
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                    >
                        See quarter details →
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Detail view: full quarterly delta table with sorting and drilldown
 */
function BestWorstQuarterDetail({ data, dimension, topicId, dimConfig, isLoading, onClose }) {
    const [sortConfig, setSortConfig] = useState({ key: 'delta', dir: 'desc' });
    const [drilldown, setDrilldown] = useState(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);

    // Get latest quarter's data
    const latestQuarterRows = useMemo(() => {
        if (data.length === 0) return [];
        const latestQ = data[0].quarter; // sorted DESC by backend
        return data.filter(r => r.quarter === latestQ);
    }, [data]);

    const sortedRows = useMemo(() => {
        const rows = [...latestQuarterRows];
        rows.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            // NULL deltas sort last
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            if (typeof aVal === 'string') return sortConfig.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
        });
        return rows;
    }, [latestQuarterRows, sortConfig]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
        }));
    }, []);

    const handleDrilldown = useCallback(async (dimensionValue) => {
        if (drilldown?.value === dimensionValue) { setDrilldown(null); return; }
        setDrilldownLoading(true);
        try {
            const res = await apiPost('topic/dimension/posts', {
                topic_id: topicId, dimension, dimension_value: dimensionValue, limit: 50,
            });
            const result = await res.json();
            setDrilldown({ value: dimensionValue, posts: result.data || [] });
        } catch (err) {
            console.error('[BestWorstQuarterDetail] drilldown error:', err);
        } finally {
            setDrilldownLoading(false);
        }
    }, [topicId, dimension, drilldown]);

    const quarter = latestQuarterRows[0]?.quarter;

    const columns = [
        { key: 'dimension_value', label: dimConfig.itemLabel, align: 'left' },
        { key: 'nps', label: 'NPS', align: 'right' },
        { key: 'prev_nps', label: 'Prev NPS', align: 'right' },
        { key: 'delta', label: 'Delta', align: 'right' },
        { key: 'count', label: 'Reviews', align: 'right' },
    ];

    return (
        <>
            <div className="dim-detail-header">
                <h3>Quarter over Quarter — {quarter || 'Latest'}</h3>
                <button className="dim-detail-close" onClick={onClose}>← Back to Overview</button>
            </div>
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Loading...
                </div>
            ) : sortedRows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Not enough quarterly data for comparison.
                </div>
            ) : (
                <div className="location-table-wrapper">
                    <table className="location-table">
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={`${col.align === 'right' ? 'text-right' : ''} ${sortConfig.key === col.key ? `sorted-${sortConfig.dir}` : ''}`}
                                        onClick={() => handleSort(col.key)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map(row => (
                                <tr
                                    key={row.dimension_value}
                                    className={drilldown?.value === row.dimension_value ? 'selected' : ''}
                                    onClick={() => handleDrilldown(row.dimension_value)}
                                >
                                    <td>{row.dimension_value}</td>
                                    <td className={`text-right`}>
                                        <span className={`nps-badge ${npsBadgeClass(row.nps)}`}>{row.nps}</span>
                                    </td>
                                    <td className="text-right">
                                        {row.prev_nps != null ? row.prev_nps : <span className="delta-badge new">New</span>}
                                    </td>
                                    <td className="text-right">
                                        {row.delta != null ? (
                                            <span className={`delta-badge ${deltaBadgeClass(row.delta)}`}
                                                  aria-label={`NPS ${row.delta > 0 ? 'improved' : 'decreased'} by ${Math.abs(row.delta)} points`}>
                                                {row.delta > 0 ? '↑' : '↓'} {row.delta > 0 ? '+' : ''}{row.delta}
                                            </span>
                                        ) : (
                                            <span className="delta-badge new">New</span>
                                        )}
                                    </td>
                                    <td className="text-right">{row.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {drilldownLoading && <div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={24} /></div>}
            {drilldown && !drilldownLoading && (
                <div className="location-drilldown">
                    <div className="location-drilldown-header">
                        <h3>Reviews for: {drilldown.value} ({drilldown.posts.length} posts)</h3>
                        <button className="location-drilldown-close" onClick={() => setDrilldown(null)}>Close</button>
                    </div>
                    <FilteredPostsDisplay
                        posts={drilldown.posts.map(p => ({
                            id: p.post_id, title: p.title, text: p.text, url: p.url,
                            time: p.time, type: p.type, score: p.score, rating: p.rating,
                        }))}
                        isLoading={false}
                    />
                </div>
            )}
        </>
    );
}

BestWorstQuarterSummary.Detail = BestWorstQuarterDetail;
export default BestWorstQuarterSummary;
